
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar';
import Footer from '../../components/Footer';
import { Shield, Mail, Lock, Info, AlignLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16 w-full">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center gap-2 mb-8 text-center">
            <Shield className="h-6 w-6 text-synthreo-blue" />
            <h1 className="text-3xl font-bold tracking-tight">Synthreo Privacy Policy</h1>
          </div>
          
          <div className="prose prose-blue max-w-none">
            <p className="text-lg text-muted-foreground mb-8 text-center">
              Last Updated: May 18, 2025
            </p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Introduction</h2>
              <p className="text-left">
                At Synthreo, we value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services, including the Synthreo Platform (ThreoAI, ThreoBuilder, and our Admin Portal), and any Managed AI Services.
              </p>
              <p className="text-left">
                By using our services, you agree to this Privacy Policy, our Terms and Conditions, and any accepted Quotes, all of which form a binding agreement between you and Synthreo.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Who We Are</h2>
              <p className="text-left">
                Synthreo provides AI-driven software and Managed AI Services to businesses and service providers. We act as a data processor, processing personal information on behalf of our clients and only on their documented instructions. Where required, a Data Processing Agreement (DPA) is available.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Information We Collect</h2>
              <p className="text-left">We collect several types of information from and about users of our website and services, including:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li><strong>Information you provide to us directly:</strong> Name, email address, phone number, company name, and any communications you send.</li>
                <li><strong>Information collected automatically:</strong> IP address, device type, browser type, usage data, and cookie-based interactions.</li>
                <li><strong>Information from third parties:</strong> Such as integrations you enable or platforms you connect.</li>
              </ul>
              <p className="mt-4 text-left">
                You may not submit sensitive personal information (e.g., payment card numbers, government-issued IDs, health or biometric data) unless expressly authorized in writing. We may delete such data without notice.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Third-Party Systems and Integrations</h2>
              <p className="text-left">
                Synthreo may support integrations with third-party platforms (e.g., CRMs, help desks) at your direction. These are subject to their own privacy and security policies.
              </p>
              <p className="mt-3 text-left">
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Ensuring you have valid rights and permissions;</li>
                <li>Managing security settings and compliance;</li>
                <li>Understanding that Synthreo is not liable for any breach or misuse involving third-party systems.</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Analytics and Cookies</h2>
              <p className="text-left">
                We use the following analytics tools to understand site usage and improve performance:
              </p>
              
              <div className="mt-6 space-y-4 text-left">
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-synthreo-blue-lightest p-1 rounded">
                    <Info className="h-4 w-4 text-synthreo-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium">Google Analytics 4</h3>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-synthreo-blue-lightest p-1 rounded">
                    <Info className="h-4 w-4 text-synthreo-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium">Google Tag Manager</h3>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-synthreo-blue-lightest p-1 rounded">
                    <Info className="h-4 w-4 text-synthreo-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium">Microsoft Clarity</h3>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-synthreo-blue-lightest p-1 rounded">
                    <Info className="h-4 w-4 text-synthreo-blue" />
                  </div>
                  <div>
                    <h3 className="font-medium">Koala Analytics</h3>
                  </div>
                </div>
              </div>
              
              <p className="mt-4 text-left">
                These tools collect anonymized usage data and do not combine analytics with personal identifiers unless you provide explicit consent. We use cookies categorized as:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li><strong>Essential:</strong> Necessary for basic site functionality</li>
                <li><strong>Analytics:</strong> Measure site performance and usage trends</li>
                <li><strong>Marketing:</strong> Support ad delivery and optimization</li>
              </ul>
              <p className="mt-4 text-left">
                You can manage your cookie preferences via our{" "}
                <span 
                  className="text-synthreo-blue hover:underline cursor-pointer"
                  onClick={() => {
                    const event = new CustomEvent('openCookieSettings');
                    window.dispatchEvent(event);
                  }}
                >
                  Cookie Settings
                </span>. Disabling cookies may limit some features.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Use of AI Systems</h2>
              <p className="text-left">
                Synthreo's services include features powered by artificial intelligence (AI) and large language models. These systems:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Operate probabilistically and may produce varied outputs;</li>
                <li>Are not guaranteed to be accurate or suitable for critical use;</li>
                <li>Require qualified human review before being used in high-risk areas (e.g., legal, employment, medical).</li>
              </ul>
              <p className="mt-4 text-left">
                You agree not to rely solely on AI outputs for business-critical decisions.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">How We Use Your Information</h2>
              <p className="text-left">We use your data to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Provide, maintain, and support our platform and services;</li>
                <li>Deliver Managed AI Services as specified in your Quotes;</li>
                <li>Communicate with you about your account or activity;</li>
                <li>Prevent abuse, detect fraud, and monitor security;</li>
                <li>Improve features through anonymized insights.</li>
              </ul>
              <p className="mt-4 text-left">
                We only process personal data based on your documented instructions and in compliance with contractual terms.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Use of Anonymized Data for Training</h2>
              <p className="text-left">
                Unless you opt out, we may use non-sensitive, anonymized, and aggregated data derived from your usage to improve our AI systems. This data:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Is stripped of all personal identifiers;</li>
                <li>Cannot be linked back to you or your organization;</li>
                <li>Is used strictly in compliance with laws such as GDPR and CCPA.</li>
              </ul>
              <p className="mt-4 text-left">
                To opt out, contact <a href="mailto:privacy@synthreo.ai" className="text-synthreo-blue hover:underline">privacy@synthreo.ai</a>. Opting out may reduce access to some AI features.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Data Retention and Termination</h2>
              <p className="text-left">
                We retain your data for the duration of your active services and for up to 30 days after formal cancellation. Simply stopping use does not constitute valid termination.
              </p>
              <p className="mt-3 text-left">
                To terminate services, written notice must be submitted to <a href="mailto:billing@synthreo.ai" className="text-synthreo-blue hover:underline">billing@synthreo.ai</a> at least 90 days in advance. Upon request, we will:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Permanently delete your data; or</li>
                <li>Return it in a commonly used format (e.g., CSV, JSON).</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Your Data Protection Rights</h2>
              <p className="text-left">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>Access your personal data;</li>
                <li>Correct or update inaccuracies;</li>
                <li>Request deletion;</li>
                <li>Restrict or object to processing;</li>
                <li>Request data portability;</li>
                <li>Withdraw consent where applicable.</li>
              </ul>
              <p className="mt-4 text-left">
                To exercise these rights, email <a href="mailto:privacy@synthreo.ai" className="text-synthreo-blue hover:underline">privacy@synthreo.ai</a>. We will respond as required by law.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Security Measures</h2>
              <p className="text-left">Synthreo applies a range of technical and organizational safeguards:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>AES-256 encryption at rest and TLS 1.2+ in transit</li>
                <li>Logical tenant data separation</li>
                <li>24/7 monitored infrastructure</li>
                <li>Prompt injection protection and output filtering</li>
                <li>Continuous vulnerability management</li>
              </ul>
              <p className="mt-4 text-left">
                Despite our efforts, no system is 100% secure. You are responsible for maintaining secure endpoints, user access, and integration environments.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Age Restrictions</h2>
              <p className="text-left">
                Synthreo services are not intended for individuals under the age of 18. By using our platform, you confirm you meet the minimum age requirement.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Changes to This Privacy Policy</h2>
              <p className="text-left">We may update this Privacy Policy. When we do:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 text-left">
                <li>The "Last Updated" date will reflect the change;</li>
                <li>We will post the updated version on this page;</li>
                <li>We may notify you via email or alert if changes are material.</li>
              </ul>
              <p className="mt-4 text-left">
                By continuing to use our services, you agree to the updated policy.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-left">Contact Us</h2>
              <div className="space-y-4 text-left">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-synthreo-blue" />
                  <p>For questions or to exercise your privacy rights, contact: <a href="mailto:privacy@synthreo.ai" className="text-synthreo-blue hover:underline">privacy@synthreo.ai</a></p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-synthreo-blue" />
                  <p>For termination or billing-related matters, contact: <a href="mailto:billing@synthreo.ai" className="text-synthreo-blue hover:underline">billing@synthreo.ai</a></p>
                </div>
              </div>
              <div className="mt-8 text-left">
                <Link to="/company/contact" className="text-synthreo-blue hover:underline">
                  Go to our contact page
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
